/*
  FE WISHLIST HEART
  ---------------------------------------------------------------------------
  Registers the product-page wishlist heart as <wishlist-button-product>.

  Swish only registers that element on templates that use its own theme app block. The
  mens-ring and couples-set sections are hand-built and use no app blocks, so nothing registers
  it there and the heart would never render. This mirrors the app's own loader (extension asset
  block-wishlist-button-collection.js) to register it ourselves.

  The element NAME is not ours to choose: every rule in the app's
  component-wishlist-button-product.css is scoped to `wishlist-button-product`. Register it
  under any other name and the app's styling silently does not apply — the icon loses
  `--icon-fill: transparent` (so it renders as a solid black blob) and never picks up
  `.wk-selected { --icon-fill: var(--icon-stroke) }`, so saving a ring does not fill the heart.

  The component itself comes from assets/fe-wishlist-button-product.js rather than the app's
  built-in one, so that an unbound heart stays unbound and a click always saves the size that is
  actually selected. See that file for why. The app's stylesheet is still used as-is.

  Binding the chosen size
  ---------------------------------------------------------------------------
  The custom size and width pickers change the buy form's hidden input by assigning .value,
  which fires no event and leaves no mutation record, so we re-check after any interaction.
  Writing data-variant-id is enough on its own — the app watches the dataset. Do NOT remove and
  re-insert the element to force a refresh: that tears down its state subscription and roughly
  triples the time it takes to catch up.

  Crucially, the hidden input is NOT evidence that a size was chosen: it is pre-filled with the
  first available variant on page load. Reading it on any interaction (a gallery click, opening
  the dropdown, even clicking the heart) would silently bind size 4 for a shopper who chose
  nothing. So data-fe-choice names the size picker itself, and nothing is bound until that
  control actually holds a value. Do not substitute a looser test such as "some select on the
  page has a value" — the options app injects its own dropdowns into this section and they
  satisfy that immediately.
*/
(() => {
  if (window.__feWishlistHeart) return;
  window.__feWishlistHeart = true;

  const ELEMENT_NAME = 'wishlist-button-product';

  const register = async () => {
    if (!window.WishlistKingAppLoaderURL) return false;
    if (customElements.get(ELEMENT_NAME)) return true;

    try {
      const { default: loader, appSettings } = await import(window.WishlistKingAppLoaderURL);

      loader.load({
        name: 'fe-wishlist-button-product',
        customElementName: ELEMENT_NAME,
        type: 'lit-component',
        url: window.FE_WISHLIST_BUTTON_URL || appSettings.assets.componentWishlistButtonProductJs,
        dependencies: [{ url: appSettings.assets.componentWishlistButtonProductCss, order: 5 }],
      });
    } catch (error) {
      console.warn('Wishlist heart could not load', error);
    }

    return true;
  };

  /* The app embed sets WishlistKingAppLoaderURL in the head, but it loads lazily — give it
     a few frames before giving up rather than failing silently on a slow connection. */
  const registerWhenReady = async (attempt = 0) => {
    if (await register()) return;
    if (attempt > 40) return;
    setTimeout(() => registerWhenReady(attempt + 1), 250);
  };

  /* Has the shopper actually chosen? Hearts on products with nothing to choose carry no
     data-fe-choice and are always considered chosen. */
  const hasChoice = (heart) => {
    const selector = heart.getAttribute('data-fe-choice');
    if (!selector) return true;

    const control = document.querySelector(selector);
    return !!(control && control.value);
  };

  const sync = () => {
    document.querySelectorAll('.fe-heart[data-fe-sync]').forEach((heart) => {
      if (!hasChoice(heart)) {
        // Covers clearing the picker back to "Select your size" after having picked one.
        if (heart.dataset.variantId) delete heart.dataset.variantId;
        return;
      }

      const input = document.querySelector(heart.getAttribute('data-fe-sync'));
      if (!input || !input.value) return;
      if (heart.dataset.variantId === input.value) return;

      heart.dataset.variantId = input.value;
    });
  };

  const queueSync = () => setTimeout(sync, 60);

  document.addEventListener('change', queueSync, true);
  document.addEventListener('click', queueSync, true);

  registerWhenReady();
})();
